import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay } from '@chakra-ui/react';

import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  type BackToSchool2026SaleVariant,
  BTS_2026_SALE_VARIANTS,
  getBts2026SaleCtaHref,
} from '~/features/promotions/constants/back-to-school-2026-sale';

import { BackToSchool2026UnderlinedPhrase } from './back-to-school-2026-headline';

export type BackToSchool2026PromoBannerProps = {
  variant: BackToSchool2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

function BackToSchool2026BannerBackground({
  variant,
  imagePriority,
}: {
  variant: BackToSchool2026SaleVariant;
  imagePriority?: boolean;
}) {
  const config = BTS_2026_SALE_VARIANTS[variant];

  return (
    <Box inset={0} pointerEvents='none' position='absolute'>
      <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={config.catalogBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'bottom right', pointerEvents: 'none' }}
          fill
        />
      </Box>
      <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='656px'
          src={config.catalogBg}
          style={{ objectFit: 'cover', objectPosition: 'bottom', pointerEvents: 'none' }}
          fill
        />
      </Box>
    </Box>
  );
}

/**
 * Catalog hero carousel banner for Back to School 2026 (Figma 1814:1219 desktop, 1814:1202
 * mobile). The headline inverts the homepage treatment (white line 1 over a gold line 2) and flexes
 * to fill the row so the desktop copy holds two lines instead of wrapping to four. The price phrase
 * carries the same underline art as the other two surfaces.
 */
export function BackToSchool2026PromoBanner({
  variant,
  promoCode,
  imagePriority,
  onCtaClick,
}: BackToSchool2026PromoBannerProps) {
  const config = BTS_2026_SALE_VARIANTS[variant];
  const ariaLabel = `${config.catalogHeadlineLine1} ${config.catalogHeadlineLine2}: ${config.catalogCtaLabel}`;

  return (
    <LinkBox
      background={config.catalogBackgroundGradient}
      border='1px solid'
      borderColor={config.catalogBorderColor}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <BackToSchool2026BannerBackground imagePriority={imagePriority} variant={variant} />
      <Flex
        alignItems={{ base: 'flex-start', md: 'center' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 4 }}
        height='100%'
        justifyContent='space-between'
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '16px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Heading
          as='h3'
          display='flex'
          flexDirection='column'
          flexGrow={1}
          fontSize={{ base: '1rem', md: '1.25rem' }}
          fontWeight='600'
          gap={{ base: '6px', md: 0 }}
          letterSpacing='-0.01em'
          lineHeight={{ base: 1.1, md: 1.2 }}
          minWidth={0}
        >
          <Box as='span' color='white'>
            {config.catalogHeadlineLine1}
          </Box>
          <Box as='span' color={config.headlineAccentColor}>
            <BackToSchool2026UnderlinedPhrase
              phrase={config.headlineUnderlinePhrase}
              text={config.catalogHeadlineLine2}
            />
          </Box>
        </Heading>
        <Button
          alignItems='center'
          as='span'
          backgroundColor={config.catalogCtaColor}
          borderRadius='4px'
          color='white'
          display='inline-flex'
          flexShrink={0}
          fontSize='12px'
          fontWeight='400'
          height='auto'
          leftIcon={<CircleArrowRightIcon boxSize='14px' color='white' aria-hidden />}
          lineHeight={1.4}
          paddingLeft='12px'
          paddingRight='16px'
          paddingY='10px'
          pointerEvents='none'
          tabIndex={-1}
          variant='unstyled'
          aria-hidden
        >
          {config.catalogCtaLabel}
        </Button>
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={getBts2026SaleCtaHref(promoCode)}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
